import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageContainer } from '../components/layout';
import RegisterTable from '../components/registers/RegisterTable';
import { useAuth } from '../auth/useAuth';
import { defaultScopeFor } from '../config/roles';
import type { ScopeToken } from '../lib/scope';
import { isOpen, isOverdue, isReportable, riskRatingOf } from '../lib/registerReaders';
import type { RegisterKey, RegisterRecord } from '../types/qms';
import styles from './RegisterViewsPage.module.css';

/**
 * RegisterViewsPage (04-07) — the working surface across all five registers.
 *
 * A register switcher (Incidents / Complaints / Improvements / Feedback / Risk)
 * plus that register's matrix §6 named views as segmented tabs. Each view maps to
 * a scope `viewToken` (My = `Own only`, Portfolio, All — the role-scope words) OR
 * layers an extra status/reportable/rating predicate ON TOP of the role's default scope
 * (Open-Overdue / Reportable / Open-In-Progress / Compliments / Review due). Risk's "By rating"
 * groups the role-scoped rows into a heatmap-ish list of per-rating tables (residual rating,
 * else inherent). Every predicate reads through `lib/registerReaders.ts` (13-16).
 *
 * The default view is the role's default scope (Oversight → All; CareManager →
 * Portfolio with the grouped fallback, 04-CONTEXT §8). It reads the `?register=`
 * query param so tiles (04-05) deep-link straight into the matching register
 * (`/registers?register=<key>`).
 *
 * Renders the single `RegisterTable`, so every list scopes through the one engine
 * (`applyScope`) — consistent visibility with the tiles and the closure trail.
 * Presentation-only; honest empty states against today's empty registers.
 */

// ---------------------------------------------------------------------------
// Register switcher metadata (CMA-language labels, inline — 04-CONTEXT §5)
// ---------------------------------------------------------------------------

const REGISTER_ORDER: RegisterKey[] = [
  'incidents',
  'complaints',
  'ci',
  'feedback',
  'risk',
];

const REGISTER_LABELS: Record<RegisterKey, string> = {
  incidents: 'Incidents',
  complaints: 'Complaints',
  ci: 'Improvements',
  feedback: 'Feedback',
  risk: 'Risks',
};

// ---------------------------------------------------------------------------
// Named-view model (matrix §6)
// ---------------------------------------------------------------------------

/**
 * A named view = an id + label + how it scopes. `forceToken` overrides the role
 * scope (My / Portfolio / All); otherwise the role's default token is used.
 * `extraPredicate` layers a status/reportable filter on top. `byRating` flags the Risk
 * heatmap grouping (rendered specially).
 */
interface NamedView {
  id: string;
  label: string;
  /** Force a specific scope token (else the role's default scope is used). */
  forceToken?: ScopeToken;
  /** Narrow on top of the role scope (status / reportable predicate). */
  extraPredicate?: (record: RegisterRecord) => boolean;
  /** Risk only: render grouped-by-rating tables. */
  byRating?: boolean;
  /** Empty-state line tailored to the view. */
  emptyMessage?: string;
}

// --- shared predicates (all via lib/registerReaders.ts) --------------------

/** Open OR overdue — the "Open / Overdue" matrix view for Incidents + CI. */
function makeOpenOverdue(now: number) {
  return (record: RegisterRecord) => isOpen(record) || isOverdue(record, now);
}

function isCompliment(record: RegisterRecord): boolean {
  return (record as { category?: string | null }).category === 'Compliment';
}

/**
 * Review due (Risk): open, with a `ReviewDate` before today (Melbourne days) — the live
 * `Review Due` view's filter (`ReviewDate` < [Today]). It is Risk's overdue cut.
 */
function isReviewDue(record: RegisterRecord, now: number): boolean {
  return isOverdue(record, now);
}

// ---------------------------------------------------------------------------
// Per-register named views (matrix §6). Built per-render with `now` injected.
// ---------------------------------------------------------------------------

const MY_VIEW = (label: string): NamedView => ({
  id: 'my',
  label,
  forceToken: 'Own only',
  emptyMessage: 'Nothing you’ve lodged shows here yet.',
});

const PORTFOLIO_VIEW: NamedView = {
  id: 'portfolio',
  label: 'Portfolio',
  forceToken: 'Portfolio',
  emptyMessage:
    'No items in your portfolio yet. Grouped by managing unit until per-CM portfolios are configured.',
};

const ALL_VIEW: NamedView = {
  id: 'all',
  label: 'All — Oversight',
  forceToken: 'All',
  emptyMessage: 'No records in this register yet.',
};

function viewsFor(registerKey: RegisterKey, now: number): NamedView[] {
  const openOverdue = makeOpenOverdue(now);

  switch (registerKey) {
    case 'incidents':
      return [
        MY_VIEW('My incidents'),
        PORTFOLIO_VIEW,
        ALL_VIEW,
        {
          id: 'reportable',
          label: 'Reportable (SIRS / NDIS / WorkSafe)',
          extraPredicate: isReportable,
          emptyMessage: 'No reportable incidents in this view.',
        },
        {
          id: 'open-overdue',
          label: 'Open / Overdue',
          extraPredicate: openOverdue,
          emptyMessage: 'Nothing open or overdue here — all caught up.',
        },
      ];
    case 'ci':
      return [
        MY_VIEW('My improvements'),
        PORTFOLIO_VIEW,
        ALL_VIEW,
        {
          id: 'open-overdue',
          label: 'Open / Overdue',
          extraPredicate: openOverdue,
          emptyMessage: 'Nothing open or overdue here — all caught up.',
        },
      ];
    case 'complaints':
      return [
        MY_VIEW('My complaints'),
        PORTFOLIO_VIEW,
        ALL_VIEW,
        {
          id: 'open-in-progress',
          label: 'Open / In progress',
          extraPredicate: isOpen,
          emptyMessage: 'No open or in-progress complaints in this view.',
        },
      ];
    case 'feedback':
      return [
        MY_VIEW('My feedback'),
        PORTFOLIO_VIEW,
        ALL_VIEW,
        {
          id: 'compliments',
          label: 'Compliments',
          extraPredicate: isCompliment,
          emptyMessage: 'No compliments recorded in this view yet.',
        },
      ];
    case 'risk':
      return [
        MY_VIEW('My risks'),
        {
          id: 'by-rating',
          label: 'By rating',
          byRating: true,
          emptyMessage: 'No risks rated yet.',
        },
        {
          id: 'review-due',
          label: 'Review due',
          extraPredicate: (r) => isReviewDue(r, now),
          emptyMessage: 'No risks are due for review.',
        },
        ALL_VIEW,
      ];
    default:
      return [MY_VIEW('Mine'), PORTFOLIO_VIEW, ALL_VIEW];
  }
}

// ---------------------------------------------------------------------------
// Risk "By rating" — grouped heatmap-ish list (highest rating first)
// ---------------------------------------------------------------------------

const RATING_ORDER = ['Extreme', 'High', 'Medium', 'Low', 'Unrated'];

/** The bucket a risk falls in: residual rating, else inherent, else Unrated. */
function ratingBucketOf(record: RegisterRecord): string {
  return riskRatingOf(record)?.rating ?? 'Unrated';
}

/**
 * Render Risk grouped by rating: one RegisterTable per rating bucket, each
 * narrowed by an extraPredicate to that bucket, ordered highest rating first.
 * Buckets are derived from the role-scoped data so we don't show empty headings
 * — but we always at least render the table component (it shows its own empty
 * state) once a bucket is known to exist.
 */
function ByRatingGroups({
  registerKey,
  viewToken,
}: {
  registerKey: RegisterKey;
  viewToken: ScopeToken;
}) {
  // We don't have the scoped rows here (RegisterTable owns the data), so render
  // the standard set of buckets in rating order; each table self-filters and
  // shows its own honest empty state when a bucket has no rows.
  return (
    <div className={styles.ratingGroups}>
      {RATING_ORDER.map((rating) => (
        <section key={rating} className={styles.ratingGroup}>
          <h3 className={styles.ratingHeading}>
            <span
              className={`${styles.ratingDot} ${styles[`dot-${rating.toLowerCase()}`] ?? ''}`}
              aria-hidden="true"
            />
            {rating}
          </h3>
          <RegisterTable
            registerKey={registerKey}
            viewToken={viewToken}
            extraPredicate={(r) => ratingBucketOf(r) === rating}
            emptyMessage={`No ${rating.toLowerCase()} risks in this view.`}
          />
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function isRegisterKey(value: string | null): value is RegisterKey {
  return value !== null && (REGISTER_ORDER as string[]).includes(value);
}

export default function RegisterViewsPage() {
  const { roles } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Stable per-mount "now" for time-relative views (React-pure lazy init).
  const [now] = useState(() => Date.now());

  // Pre-select the register from `?register=<key>` (tile deep-link contract,
  // 04-05). Defaults to Incidents.
  const paramRegister = searchParams.get('register');
  const initialRegister: RegisterKey = isRegisterKey(paramRegister)
    ? paramRegister
    : 'incidents';

  const [registerKey, setRegisterKey] = useState<RegisterKey>(initialRegister);

  // Keep the selected register in sync if the query param changes (e.g. the user
  // clicks a different tile while this page is mounted).
  useEffect(() => {
    if (isRegisterKey(paramRegister) && paramRegister !== registerKey) {
      setRegisterKey(paramRegister);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramRegister]);

  const views = useMemo(() => viewsFor(registerKey, now), [registerKey, now]);

  // The active named view — reset to the register's default (first view) when
  // the register changes.
  const [activeViewId, setActiveViewId] = useState<string>(views[0].id);
  useEffect(() => {
    setActiveViewId(views[0].id);
  }, [views]);

  const activeView = views.find((v) => v.id === activeViewId) ?? views[0];

  // The role's default scope for this register (the base visibility). A named
  // view may force a different token; otherwise this is used. `null` (role can't
  // view) fails closed at the scope engine (renders empty).
  const roleDefaultToken = defaultScopeFor(roles, registerKey) ?? 'Own only';
  const viewToken: ScopeToken = activeView.forceToken ?? roleDefaultToken;

  const selectRegister = (key: RegisterKey) => {
    setRegisterKey(key);
    // Reflect the selection in the URL so the deep-link contract round-trips.
    setSearchParams({ register: key }, { replace: true });
  };

  return (
    <PageContainer title="Registers" maxWidth="full">
      <p className={styles.intro}>
        Work the five registers — scoped to what your role manages. Switch
        register, then choose a view.
      </p>

      {/* ── Register switcher ── */}
      <div className={styles.registerTabs} role="tablist" aria-label="Register">
        {REGISTER_ORDER.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={key === registerKey}
            className={`${styles.registerTab} ${
              key === registerKey ? styles.registerTabActive : ''
            }`}
            onClick={() => selectRegister(key)}
          >
            {REGISTER_LABELS[key]}
          </button>
        ))}
      </div>

      {/* ── Named-view segments ── */}
      <div className={styles.viewSegments} role="tablist" aria-label="View">
        {views.map((view) => (
          <button
            key={view.id}
            type="button"
            role="tab"
            aria-selected={view.id === activeViewId}
            className={`${styles.viewSegment} ${
              view.id === activeViewId ? styles.viewSegmentActive : ''
            }`}
            onClick={() => setActiveViewId(view.id)}
          >
            {view.label}
          </button>
        ))}
      </div>

      {/* ── The list ── */}
      {activeView.byRating ? (
        <ByRatingGroups registerKey={registerKey} viewToken={viewToken} />
      ) : (
        <RegisterTable
          key={`${registerKey}:${activeView.id}`}
          registerKey={registerKey}
          viewToken={viewToken}
          extraPredicate={activeView.extraPredicate}
          emptyMessage={activeView.emptyMessage}
        />
      )}
    </PageContainer>
  );
}
